import React, { useEffect, useState } from 'react';
import { FiSearch, FiMapPin, FiNavigation } from 'react-icons/fi';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../../libs/axios';
import { Pagination, PaginationType } from '../../../utils/pagination.utils';
import { ExcelDownload } from '../../../utils/excel.utils';
import { dateRangeError, dateRangeInputBounds } from '../../../utils/date-range.utils';
import { formatDate } from '../../activities/edit';

interface Engineer {
  id: number;
  name: string;
  employee_code: string;
  association_type: string;
}

/** The API returns nested objects for project / state_circle; older builds returned plain strings. */
type NamedRef =
  | string
  | { id?: number; project_name?: string; project_short_name?: string; state_name?: string }
  | null;

interface ProjectSite {
  id: number;
  site_id: string;
  site_name: string;
  reference_number: string | null;
  work_date: string;
  project: NamedRef;
  state_circle: NamedRef;
}

interface LocationPoint {
  latitude: number;
  longitude: number;
  address: string;
  site_id?: string;
  site_name?: string;
}

interface Distances {
  reporting_to_checkin_km: number;
  checkin_to_site_km: number;
  site_to_checkout_km: number;
  checkout_to_reporting_km: number;
  total_route_km: number;
  site_distance_from_attendance_km: number;
  site_distance_from_reporting_km: number;
  final_shortest_distance_km: number;
}

interface ProjectClaimRow {
  id: number;
  engineer: Engineer;
  project_site: ProjectSite;
  status: string;
  travel_mode: string;
  claim_amount?: number | null;
  location_trail: {
    reporting: LocationPoint;
    checkin: LocationPoint;
    site: LocationPoint;
    checkout: LocationPoint;
  };
  distances: Distances;
  amounts: { submitted: any; approved: any };
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const TABLE_COLUMN_COUNT = 12;

/** Reads a display label out of either a plain string or a nested {project_name}/{state_name} object. */
const refLabel = (ref: NamedRef): string => {
  if (!ref) return '';
  if (typeof ref === 'string') return ref;
  return ref.project_name || ref.state_name || '';
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/** Approved total wins; falls back to what the engineer submitted. */
const claimAmountOf = (claim: ProjectClaimRow): number | null =>
  toNumber(claim.claim_amount) ??
  toNumber(claim.amounts?.approved?.total) ??
  toNumber(claim.amounts?.submitted?.total);

const formatAmount = (value: number | null, fallback = '-') =>
  value === null
    ? fallback
    : `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ProjectClaimReport: React.FC = () => {
  const [claims, setClaims] = useState<ProjectClaimRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: 10, has_next_page: false, has_previous_page: false });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilterForm, setShowFilterForm] = useState(true);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);

  const formatDateValue = (value?: string | null, fallback = '-') => (value ? formatDate(value) : fallback);

  const buildParams = (targetPage: number, targetLimit: number) => {
    const params: any = { page: targetPage, limit: targetLimit };
    if (search.trim()) params.search = search.trim();
    if (status) params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const fetchClaims = async (targetPage: number = page, targetLimit: number = pagination.items_per_page) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/claims/project', { params: buildParams(targetPage, targetLimit) });
      if (response.data.success) {
        const payload = response.data.data || { claims: [] };
        const rows = payload.claims || [];
        setClaims(rows);
        const p = payload.pagination || {};
        const total = Number(p.total_items ?? p.total_count) || rows.length;
        const pages = Number(p.total_pages) || Math.max(1, Math.ceil(total / targetLimit));
        setPagination({
          total_items: total,
          current_page: targetPage,
          total_pages: pages,
          items_per_page: targetLimit,
          has_next_page: targetPage < pages,
          has_previous_page: targetPage > 1
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch project claim reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasSearched) return;
    fetchClaims(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hasSearched]);

  const mapClaimForExport = (c: ProjectClaimRow) => ({
    'Claim ID': c.id,
    'Engineer ID': c.engineer?.employee_code || c.engineer?.id || '',
    'Engineer Name': c.engineer?.name || '',
    Association: c.engineer?.association_type || '',
    'Site ID': c.project_site?.site_id || '',
    'Site Name': c.project_site?.site_name || '',
    'Reference No': c.project_site?.reference_number || '',
    Project: refLabel(c.project_site?.project),
    'State Circle': refLabel(c.project_site?.state_circle),
    'Work Date': c.project_site?.work_date ? formatDate(c.project_site.work_date) : '',
    'Travel Mode': c.travel_mode || '',
    Status: c.status || '',
    'Submitted Amount': toNumber(c.amounts?.submitted?.total) ?? '',
    'Approved Amount': toNumber(c.amounts?.approved?.total) ?? '',
    'Claim Amount': claimAmountOf(c) ?? '',
    'Total Route (km)': c.distances?.total_route_km ?? '',
    'Shortest Distance (km)': c.distances?.final_shortest_distance_km ?? '',
  });

  const fetchExportData = async () => {
    // The filter form can be edited after a search, so the export checks it too.
    const dateError = dateRangeError(dateFrom, dateTo, 'Date range');
    if (dateError) {
      toast.error(dateError);
      return;
    }
    try {
      setExporting(true);
      let currentPage = 1;
      let totalPages = 1;
      const pageSize = 100;
      const allClaims: ProjectClaimRow[] = [];

      do {
        const response = await axiosInstance.get('/reports/claims/project', {
          params: buildParams(currentPage, pageSize),
        });
        if (!response.data.success) break;
        const payload = response.data.data || { claims: [] };
        const rows: ProjectClaimRow[] = payload.claims || [];
        allClaims.push(...rows);
        const p = payload.pagination || {};
        totalPages =
          Number(p.total_pages) ||
          Math.max(1, Math.ceil((Number(p.total_items ?? p.total_count) || allClaims.length) / pageSize));
        currentPage += 1;
      } while (currentPage <= totalPages);

      setExportData(allClaims.map(mapClaimForExport));
      setShowExcelModal(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to prepare project claim export');
    } finally {
      setExporting(false);
    }
  };

  const handleApplyFilters = () => {
    const dateError = dateRangeError(dateFrom, dateTo, 'Date range');
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setPage(1);
    setExpandedRow(null);
    setHasSearched(true);
    setShowFilterForm(false);
    fetchClaims(1, pagination.items_per_page);
  };

  const handleResetDraftFilters = () => {
    setSearch('');
    setStatus('');
    setDateFrom('');
    setDateTo('');
  };

  const handleClearFilters = () => {
    handleResetDraftFilters();
    setClaims([]);
    setExpandedRow(null);
    setHasSearched(false);
    setShowFilterForm(true);
    setPage(1);
    setPagination({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: pagination.items_per_page, has_next_page: false, has_previous_page: false });
  };

  const paginationAdapter: PaginationType = {
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null
  };

  const getStatusBadge = (s: string) => {
    const map: Record<string, string> = {
      submitted: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      pending: 'bg-blue-100 text-blue-700',
    };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Project Claim Report</h1>
            <p className="text-sm text-gray-600 mt-1">View project claims with location trail & distances.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showFilterForm && (
              <button
                onClick={() => setShowFilterForm(true)}
                className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Apply Filters Again
              </button>
            )}
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={fetchExportData}
              disabled={!hasSearched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {showFilterForm ? (
        <div className="bg-white shadow-sm rounded border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="relative">
              <FiSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded text-sm">
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending">Pending</option>
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} {...dateRangeInputBounds(dateFrom, dateTo).from} className="px-3 py-2 border border-gray-300 rounded text-sm" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} {...dateRangeInputBounds(dateFrom, dateTo).to} className="px-3 py-2 border border-gray-300 rounded text-sm" />
            <div className="flex items-center gap-2">
              <button onClick={handleApplyFilters} className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">Apply Filters</button>
              <button onClick={handleResetDraftFilters} className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">Reset</button>
            </div>
          </div>
        </div>
      ) : (
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Engineer ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Engineer Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Site ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Site Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Reference No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Work Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Travel Mode</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 whitespace-nowrap">Claim Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Total Route (km)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Shortest Dist (km)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-10 text-center text-sm text-gray-600">Loading...</td></tr> :
                claims.length === 0 ? <tr><td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-10 text-center text-sm text-gray-600">No claims found.</td></tr> :
                  claims.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{c.engineer?.employee_code || c.engineer?.id || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {c.engineer?.name || '-'}
                          {c.engineer?.association_type && (
                            <div className="text-xs text-gray-500 capitalize">{c.engineer.association_type}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{c.project_site?.site_id || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.project_site?.site_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{c.project_site?.reference_number || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDateValue(c.project_site?.work_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 capitalize">{c.travel_mode || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium capitalize ${getStatusBadge(c.status)}`}>{c.status}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium text-right whitespace-nowrap">{formatAmount(claimAmountOf(c))}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">{c.distances?.total_route_km ?? '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">{c.distances?.final_shortest_distance_km ?? '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <button onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)} className="p-2 rounded hover:bg-gray-100 text-blue-700">
                            <FiMapPin className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {expandedRow === c.id && (
                        <tr>
                          <td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-4 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><FiNavigation className="w-4 h-4" /> Location Trail</h4>
                                <div className="space-y-2 text-xs">
                                  {(['reporting', 'checkin', 'site', 'checkout'] as const).map(point => {
                                    const loc = c.location_trail?.[point];
                                    return loc ? (
                                      <div key={point} className="bg-white border border-gray-200 rounded p-2">
                                        <span className="font-medium text-gray-700 capitalize">{point}:</span>
                                        <span className="text-gray-600 ml-1">{loc.address || `${loc.latitude}, ${loc.longitude}`}</span>
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><FiMapPin className="w-4 h-4" /> Distances</h4>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="bg-white border border-gray-200 rounded p-2">
                                    <span className="text-gray-500">Reporting → Checkin</span>
                                    <div className="font-medium">{c.distances?.reporting_to_checkin_km ?? '-'} km</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded p-2">
                                    <span className="text-gray-500">Checkin → Site</span>
                                    <div className="font-medium">{c.distances?.checkin_to_site_km ?? '-'} km</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded p-2">
                                    <span className="text-gray-500">Site → Checkout</span>
                                    <div className="font-medium">{c.distances?.site_to_checkout_km ?? '-'} km</div>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded p-2">
                                    <span className="text-gray-500">Checkout → Reporting</span>
                                    <div className="font-medium">{c.distances?.checkout_to_reporting_km ?? '-'} km</div>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Claim Amount</h4>
                                <div className="space-y-2 text-xs">
                                  <div className="bg-white border border-gray-200 rounded p-2 flex items-center justify-between">
                                    <span className="text-gray-500">Submitted total</span>
                                    <span className="font-medium">{formatAmount(toNumber(c.amounts?.submitted?.total))}</span>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded p-2 flex items-center justify-between">
                                    <span className="text-gray-500">Approved total</span>
                                    <span className="font-medium">{formatAmount(toNumber(c.amounts?.approved?.total))}</span>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded p-2 flex items-center justify-between">
                                    <span className="text-gray-500">Reference No</span>
                                    <span className="font-medium">{c.project_site?.reference_number || '-'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={paginationAdapter}
          onPageChange={(nextPage) => {
            if (!hasSearched) return;
            setPage(nextPage);
          }}
          onLimitChange={(nextLimit) => {
            setPagination(prev => ({ ...prev, items_per_page: nextLimit }));
            setPage(1);
            if (!hasSearched) return;
            fetchClaims(1, nextLimit);
          }}
        />
      </div>
      )}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="project_claim_reports"
        title="Export Project Claim Reports"
        description="Download project claim reports with current filters"
      />
    </div>
  );
};

export default ProjectClaimReport;